/**
 * Auto-fit text inside a DOM element. Copied from WhatIs ui/fit-text.ts —
 * binary-searches font-size between [min, max] until content fits the box,
 * with a ResizeObserver re-fit on geometry change. Used by the lobby +
 * side-leaderboard panels so long display names shrink instead of truncate.
 */

const MIN_DEFAULT = 10;
const MAX_DEFAULT = 64;

export interface FitTextOptions {
  min?: number;
  max?: number;
  lineHeight?: number;
  observe?: boolean;
}

interface AttachedState {
  el: HTMLElement;
  opts: Required<FitTextOptions>;
  observer: ResizeObserver | null;
  rafToken: number | null;
}

const ATTACHED = new WeakMap<HTMLElement, AttachedState>();

export function fitText(el: HTMLElement, opts: FitTextOptions = {}): number {
  const min = opts.min ?? MIN_DEFAULT;
  const max = opts.max ?? MAX_DEFAULT;
  const lineHeight = opts.lineHeight ?? 1.1;
  const observe = opts.observe ?? true;

  const resolved: Required<FitTextOptions> = { min, max, lineHeight, observe };

  const w = el.clientWidth;
  const h = el.clientHeight;
  if (w === 0 || h === 0) {
    requestAnimationFrame(() => fitText(el, opts));
    if (observe) attach(el, resolved);
    return max;
  }

  el.style.lineHeight = String(lineHeight);
  if (!el.style.overflow) el.style.overflow = 'hidden';

  const fits = (size: number): boolean => {
    el.style.fontSize = `${size}px`;
    return el.scrollWidth <= el.clientWidth + 1 && el.scrollHeight <= el.clientHeight + 1;
  };

  let lo = min;
  let hi = max;
  if (fits(hi)) {
    el.style.fontSize = `${hi}px`;
    if (observe) attach(el, resolved);
    return hi;
  }
  if (!fits(lo)) {
    el.style.fontSize = `${lo}px`;
    if (observe) attach(el, resolved);
    return lo;
  }
  while (hi - lo > 0.5) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid;
    else hi = mid;
  }
  el.style.fontSize = `${Math.floor(lo)}px`;
  if (observe) attach(el, resolved);
  return Math.floor(lo);
}

function attach(el: HTMLElement, opts: Required<FitTextOptions>): void {
  const existing = ATTACHED.get(el);
  if (existing) {
    existing.opts = opts;
    return;
  }
  if (typeof ResizeObserver === 'undefined') {
    ATTACHED.set(el, { el, opts, observer: null, rafToken: null });
    return;
  }
  const state: AttachedState = { el, opts, observer: null, rafToken: null };
  const observer = new ResizeObserver(() => {
    if (state.rafToken !== null) return;
    state.rafToken = requestAnimationFrame(() => {
      state.rafToken = null;
      fitText(state.el, { ...state.opts, observe: false });
    });
  });
  observer.observe(el);
  state.observer = observer;
  ATTACHED.set(el, state);
}
